import * as React from 'react';
import { isEmpty } from 'lodash';
import { Mutation } from 'react-apollo';
import { ApolloError } from 'apollo-client';
import pluralize from 'pluralize';

import Columns2 from 'components/Columns2';
import openUrl from 'utils/openUrl';
import LinkConstants from 'constants/LinkConstants';
import ConfirmDialog from 'components/ConfirmDialog';
import ModalTrigger from 'components/ModalTrigger';
import Box from 'components/Box';
import BoxHeader from 'components/BoxHeader';
import BoxBody from 'components/BoxBody';
import Buttons from 'components/Buttons';
import Button from 'components/Button';
import SEND_ACTIVATION_NOTIFICATIONS_MUTATION from 'admin/rostering/mutations/SendActivationNotificationsMutation.gql';
import ToastActions from 'actions/ToastActions';

import {
  sendActivationNotifications as sendActivationNotificationsResult,
  sendActivationNotificationsVariables,
} from 'admin/rostering/mutations/__generated__/sendActivationNotifications';
import { SendActivationNotificationsInput } from 'globalTypes';

type Props = {
  orgUuid: string;
  canNotifyTeachers: boolean;
  canNotifyStudents: boolean;
  canNotifyParents: boolean;
};

type State = {
  notifiedGroups: string[];
};

function rolesDescription(roles: string[]) {
  return roles.map(r => pluralize(r)).join(' and ');
}

class AdminSisSyncNotificationOptions extends React.Component<Props, State> {
  state: State = {
    notifiedGroups: [],
  };

  handleConfirmSendNotificationsToRoles = (roles: string[], mutationFn: () => void) => {
    const { notifiedGroups } = this.state;

    this.setState({
      notifiedGroups: [...notifiedGroups, ...roles],
    });

    mutationFn();
  };

  previewMessage(roles: string[]) {
    if (roles.includes('teacher')) {
      openUrl(LinkConstants.CLEVER_TEACHER_NOTIFICATION);
    } else if (roles.includes('student') || roles.includes('parent')) {
      openUrl(LinkConstants.CLEVER_STUDENT_NOTIFICATION);
    }
  }

  renderSendNotificationsModal(roles: string[], notificationType: string, mutationFn: () => void) {
    const rolesString = rolesDescription(roles);

    return (
      <ConfirmDialog
        title={`Notify ${rolesString}`}
        cancelButtonText="Cancel"
        onConfirm={() => this.handleConfirmSendNotificationsToRoles(roles, mutationFn)}
        subtext={`We’ll send ${notificationType} to the ${rolesString} in your school to claim and use their Remind accounts.`}
        data-test={'confirm-dialog'}
      />
    );
  }

  handleComplete = ({ sendActivationNotifications }: sendActivationNotificationsResult) => {
    const { error } = sendActivationNotifications;

    if (error) {
      ToastActions.showErrorToast(error.message);
    }
  };

  handleError = (error: ApolloError) => {
    console.error(
      'at=AdminSisSyncNotificationOptions could not send activation notifications',
      error
    );
  };

  renderNotificationOption(roles: string[], sendButtonText: string, notificationType: string) {
    const { orgUuid } = this.props;
    const rolesString = rolesDescription(roles);

    return (
      <Mutation
        mutation={SEND_ACTIVATION_NOTIFICATIONS_MUTATION}
        onCompleted={this.handleComplete}
        onError={this.handleError}
        key={`mutation-${roles.join('-')}`}>
        {(
          sendActivationNotifications: (input: {
            variables: sendActivationNotificationsVariables;
          }) => void
        ) => {
          const mutationFn = () =>
            sendActivationNotifications({ variables: { input: { orgUuid, roles } } });
          const rolesKey = roles.join('-');

          return (
            <Box key={rolesKey} data-test={`notification-box-${rolesKey}`}>
              <BoxHeader
                size="small"
                title={`Notify the ${rolesString}?`}
                subtext={`Help your ${rolesString} get started on Remind by sending them ${notificationType} with details of their new accounts.`}
                textAlign="left"
              />
              <BoxBody size="small">
                <Buttons full>
                  <Button level="secondary" onClick={() => this.previewMessage(roles)}>
                    Learn more
                  </Button>
                  <ModalTrigger
                    modal={this.renderSendNotificationsModal(roles, notificationType, mutationFn)}>
                    <Button data-test={`submit-${rolesKey}`}>{sendButtonText}</Button>
                  </ModalTrigger>
                </Buttons>
              </BoxBody>
            </Box>
          );
        }}
      </Mutation>
    );
  }

  render() {
    const { canNotifyTeachers, canNotifyStudents, canNotifyParents } = this.props;
    const { notifiedGroups } = this.state;

    const options = [];

    if (canNotifyTeachers && !notifiedGroups.includes('teacher')) {
      options.push(this.renderNotificationOption(['teacher'], 'Send email', 'an email'));
    }

    if (
      canNotifyStudents &&
      canNotifyParents &&
      !notifiedGroups.includes('student') &&
      !notifiedGroups.includes('parent')
    ) {
      options.push(
        this.renderNotificationOption(['student', 'parent'], 'Notify them', 'a message')
      );
    } else if (canNotifyStudents && !notifiedGroups.includes('student')) {
      options.push(this.renderNotificationOption(['student'], 'Notify them', 'a message'));
    } else if (canNotifyParents && !notifiedGroups.includes('parent')) {
      options.push(this.renderNotificationOption(['parent'], 'Notify them', 'a message'));
    }

    if (isEmpty(options)) {
      return null;
    }

    return <Columns2>{options}</Columns2>;
  }
}

export default AdminSisSyncNotificationOptions;
